import { useEffect } from "react";
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter as Router } from "react-router-dom";

import {
  BpThemeProvider,
  CenteredLargeSpinner,
  customIcons as icons,
  initializeIcons,
  registerIcons
} from "@bps/fluent-ui";
import { AuthenticationProvider, login } from "@bps/msal";
import { ErrorAlert } from "@components/errors/ErrorAlert";
import { config } from "@libs/config/config";
import { ReactQueryProvider } from "@libs/react-query/ReactQueryProvider";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { RoutingHub } from "./modules/routing/RoutingHub";

initializeIcons();

// register BP custom Fluent UI icons
registerIcons({
  fontFace: {
    fontFamily: "BP_custom_icons"
  },
  icons
});

const redirectUri = `${window.location.origin}${config.msal.postAuthenticateRedirectUrl}`;
const TIMEOUT_RECOVERY_ATTEMPTED_KEY = "ops.msal.timeout-recovery.attempted";

const authenticationConfig = {
  ...config.msal,
  redirectUri,
  postAuthenticateRedirectUrl: redirectUri
};

const isIframeTokenTimeoutError = (
  message?: string,
  errorCode?: string
): boolean => {
  const text = `${errorCode ?? ""} ${message ?? ""}`.toLowerCase();
  return (
    text.includes("monitor_window_timeout") ||
    text.includes("token acquisition in iframe failed due to timeout")
  );
};

const AuthenticationErrorFallback = ({
  message,
  errorCode
}: {
  message: string;
  errorCode?: string;
}) => {
  useEffect(() => {
    if (!isIframeTokenTimeoutError(message, errorCode)) {
      return;
    }

    if (sessionStorage.getItem(TIMEOUT_RECOVERY_ATTEMPTED_KEY)) {
      return;
    }

    sessionStorage.setItem(TIMEOUT_RECOVERY_ATTEMPTED_KEY, "1");
    void login({ prompt: "login", resetInteractionInProgressStatus: true });
  }, [message, errorCode]);

  if (isIframeTokenTimeoutError(message, errorCode)) {
    return (
      <CenteredLargeSpinner
        label="Refreshing sign-in session..."
        labelPosition="bottom"
      />
    );
  }

  return <ErrorAlert error={message} />;
};

const loadAppInsights = () => {
  if (config.appInsights && !config.development) {
    const appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: config.appInsights.instrumentationKey,
        enableAutoRouteTracking: true,
        extensions: [new ReactPlugin()]
      }
    });

    appInsights.loadAppInsights();
  }
};

export const App = () => {
  loadAppInsights();

  useEffect(() => {
    sessionStorage.removeItem(TIMEOUT_RECOVERY_ATTEMPTED_KEY);
  }, []);

  return (
    <BpThemeProvider styles={{ height: "100%" }}>
      <AuthenticationProvider
        config={authenticationConfig}
        renderSpinner={message => (
          <CenteredLargeSpinner label={message} labelPosition="bottom" />
        )}
        renderError={(message, errorCode) => (
          <AuthenticationErrorFallback
            message={message}
            errorCode={errorCode}
          />
        )}
      >
        <ReactQueryProvider>
          <Router basename={import.meta.env.PUBLIC_URL}>
            <RoutingHub />
          </Router>
          {config.development && <ReactQueryDevtools position="bottom-left" />}
        </ReactQueryProvider>
      </AuthenticationProvider>
    </BpThemeProvider>
  );
};
